/**
 * T6.1c (G-7) — WHICH TOOLTIP DO I USE? The convention, written down once.
 *
 * The app has two tooltip idioms and no rule, so the wave was accumulating
 * both — in some files both at once. Measured before deciding: 263 files use
 * a native `title`, 70 use this Radix component, and 24 use both. Native is
 * the incumbent by an order of magnitude, so the rule below RATIFIES the
 * dominant split rather than inventing a migration nobody can finish.
 *
 * THE RULE — what decides is whether the tooltip carries information, not
 * how important the control is:
 *
 *   Native `title`  — a REDUNDANT LABEL. The control already tells the user
 *                     what it does (a recognisable icon, adjacent text) and
 *                     the tooltip only spells that out. "Zoom in" on a
 *                     magnifier-plus. Cheap, and losing it costs nothing.
 *
 *   Radix `Tooltip` — DISCOVERY. The tooltip carries something the user
 *                     cannot get anywhere else: what a colour-only indicator
 *                     means, why a control is disabled, a keyboard shortcut,
 *                     an explanation. If the user would be stuck without it,
 *                     it must be styled, themed and keyboard-reachable, and a
 *                     native `title` is none of those — OS-timed, unstyled,
 *                     ignores dark mode, and unreachable from the keyboard on
 *                     most platforms.
 *
 * The corollary that actually catches bugs: `title` DOES NOT FIRE ON A
 * DISABLED CONTROL in Chromium. A disabled control that must explain itself
 * needs the `title` on a wrapping element, or visible text — see
 * `DataCompareCompareButton`.
 *
 * Deliberately NOT a migration. Existing native `title`s that fit the first
 * bullet are correct and stay; converting 263 files would be churn with no
 * user-visible gain. Reach for Radix when you are writing DISCOVERY content.
 */
import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        'z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground',
        // Entrance: instant-feeling fade only (no zoom/slide), ≤100ms — native
        // tooltip feel. Radix Tooltip has no plain "open" data-state (only
        // delayed-open/instant-open), so entrance stays unconditional; exit
        // is gated on data-[state=closed] and keeps the soft 150ms fade+zoom.
        'animate-in fade-in-0 duration-100',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:duration-150',
        className,
      )}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
